/**
 * Podcast Episodes Section - Constants and Enums
 * Centralized definitions to avoid magic strings throughout components
 */

/**
 * Podcast section column types
 * Identifies which column section is being rendered
 */
export const PODCAST_COLUMNS = {
  LATEST: "latest",
  TOP: "top",
} as const;

export type PodcastColumnType =
  (typeof PODCAST_COLUMNS)[keyof typeof PODCAST_COLUMNS];

/**
 * Column display labels
 * Show in column headers
 */
export const COLUMN_LABELS = {
  latest: "Latest episodes",
  top: "Top episodes",
} as const;

/**
 * Default section heading text
 * Shown above the two-column layout
 */
export const SECTION_HEADING = "Podcast" as const;

/**
 * "See all" button text and aria label
 * Parent can override but these are defaults
 */
export const SEE_ALL_BUTTON = {
  TEXT: "See all podcast episodes",
  ARIA_LABEL: "View all podcast episodes",
} as const;

/**
 * CSS class names for consistent sizing
 * Ensures icons are sized properly across all contexts
 */
export const ICON_SIZES = {
  SMALL: "h-[24px] w-[24px]", // Inline with text
  MEDIUM: "h-[44px] w-[44px]", // In card
  LARGE: "h-[64px] w-[64px]", // Standalone display
} as const;

/**
 * Grid and spacing values
 * Ensures consistent layout throughout component
 */
export const SPACING = {
  GAP_COLUMN: "gap-4 md:gap-4 mdDesktop:gap-4 lg:gap-4", // Gap between columns
  GAP_CARD: "gap-4", // Gap between cards in column
  PADDING_SECTION: "px-5 py-4", // Section padding
  PADDING_CARD: "p-4", // Card padding
} as const;

/**
 * Animation and transition durations (Tailwind duration- classes)
 */
export const TRANSITIONS = {
  FAST: "duration-200", // Normal transitions
  MEDIUM: "duration-300", // Slightly slower
} as const;

/**
 * Validation: Minimum items required for safe rendering
 * Helps with edge case handling
 */
export const VALIDATION = {
  MIN_EPISODES_PER_COLUMN: 1, // Minimum episodes to render (strict)
  RECOMMENDED_EPISODES: 2, // Recommended per column in design
  MAX_EPISODES_DISPLAYED: 4, // Maximum episodes to show without scrolling
} as const;

/**
 * Type guard: check if value is a valid PodcastColumnType
 * @param value - Value to check
 * @returns true if value is a valid column type
 */
export function isPodcastColumnType(
  value: unknown
): value is PodcastColumnType {
  return value === "latest" || value === "top";
}
